import { z } from 'zod'
import { idSchema, isoDateTimeSchema, minorNonNegativeSchema, optionalText, text } from './common.js'

/**
 * Other income: money earned outside a jobcard, typically spare-part counter
 * sales. Not attached to a car snapshot, so cannot be linked to a jobcard.
 */
export const OTHER_INCOME_KINDS = ['parts_sale', 'other'] as const
export type OtherIncomeKind = (typeof OTHER_INCOME_KINDS)[number]
export const otherIncomeKindSchema = z.enum(OTHER_INCOME_KINDS)

export const OTHER_INCOME_KIND_LABELS: Record<OtherIncomeKind, string> = {
  parts_sale: 'Parts sale',
  other: 'Other',
}

const otherIncomeFields = {
  kind: otherIncomeKindSchema,
  /** What this income was for, e.g. "Sold exhaust pipe to workshop". Max 200 characters. */
  description: text(200),
  /** The date this income was received, "YYYY-MM-DD". */
  date: z.string().min(1),
  amountBeforeVatMinor: minorNonNegativeSchema,
  vatMinor: minorNonNegativeSchema,
  /** How the payment was received: cash, card, bank transfer, or other. Defaults to cash. */
  paymentMethod: z.enum(['cash', 'card', 'bank', 'other']).default('cash'),
  notes: optionalText(1000),
}

export const otherIncomeInputSchema = z.object(otherIncomeFields)
export type OtherIncomeInput = z.infer<typeof otherIncomeInputSchema>

export const otherIncomeSchema = z.object({
  ...otherIncomeFields,
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type OtherIncome = z.infer<typeof otherIncomeSchema>

// List items use the same type as the full entity
export type OtherIncomeListItem = OtherIncome

/**
 * Query parameters for listing other income.
 * Supports filtering by kind, date range, and text search.
 */
export const otherIncomeListQuerySchema = z.object({
  kind: otherIncomeKindSchema.optional(),
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type OtherIncomeListQuery = z.infer<typeof otherIncomeListQuerySchema>
